import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GenerationCanvas } from "@/components/generation-canvas";
import { RecentGenerations } from "@/components/dashboard/recent-generations";
import { UpgradeModal } from "@/components/upgrade-modal";
import { useState } from "react";
import { toast } from "sonner";
import {
  getServerCreditBalance,
  getServerSubscriptionDetails,
  generateImageAction,
} from "@/lib/server-data";
import * as Sentry from "@sentry/react";

export const Route = createFileRoute("/_authenticated/dashboard")({
  // No loader - keeping client-side queries for non-blocking behavior (Phase 2.1)
  // Skipping Phase 2.2 (image preload) as it would reintroduce blocking
  // Phase 1.1 (eager loading) already provides similar benefits without blocking
  component: DashboardPage,
});

function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, region } = Route.useRouteContext();

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch credits client-side (CRITICAL - needed for GenerationCanvas)
  const { data: balance, isPending: balanceLoading } = useQuery({
    queryKey: ['credits', session?.user?.id],
    queryFn: getServerCreditBalance,
    staleTime: 5 * 60 * 1000,
    enabled: !!session?.user, // Only fetch when user session exists
  });

  // Fetch subscription in background (only needed for upgrade modal)
  const { data: subscription } = useQuery({
    queryKey: ['subscription', session?.user?.id],
    queryFn: () =>
      getServerSubscriptionDetails({
        data: { userId: session!.user.id },
      }),
    staleTime: 5 * 60 * 1000,
    enabled: !!session?.user, // Load in background, don't block UI
  });

  // Generate image mutation with optimistic updates
  // MUST be declared before any conditional returns (Rules of Hooks)
  const generateMutation = useMutation({
    mutationFn: async ({
      prompt,
      modelId,
      parameters,
      referenceImages,
    }: {
      prompt: string;
      modelId?: string;
      parameters?: Record<string, any>;
      referenceImages?: Record<string, string[]>;
    }) => {
      return generateImageAction({
        data: { prompt, modelId, parameters, referenceImages },
      });
    },
    onMutate: async () => {
      // Optimistically update credit count
      if (balance) {
        queryClient.setQueryData(['credits', session?.user?.id], {
          ...balance,
          credits: balance.credits - 1,
        });
      }
    },
    onSuccess: async (newGeneration) => {
      // Refetch credits to get actual server state after generation
      // This ensures the UI shows the correct balance
      queryClient.refetchQueries({
        queryKey: ['credits', session?.user?.id],
        type: 'active',
      });

      // Navigate to the new generation page
      // No need to invalidate dashboard-generations since user navigates away
      navigate({ to: `/generation/${newGeneration.id}` });
    },
    onError: (error) => {
      // Rollback optimistic update by refetching server state
      queryClient.refetchQueries({ 
        queryKey: ['credits', session?.user?.id],
        type: 'active',
      });
      
      Sentry.captureException(error, {
        extra: {
          component: "Dashboard",
          action: "generate-image",
        },
      });
      console.error("Failed to generate image:", error);
      
      // Check if it's a 402 (insufficient credits) error
      if (error instanceof Error && error.message.includes("402")) {
        setShowUpgradeModal(true);
      } else {
        toast.error("Failed to generate image", {
          description: error instanceof Error ? error.message : undefined,
        });
      }
    },
  });

  const handleGenerate = async (
    prompt: string,
    modelId?: string,
    parameters?: Record<string, any>,
    referenceImages?: Record<string, string[]>,
  ) => {
    // Check if user has credits before attempting generation
    if (!balance || !("credits" in balance) || balance.credits < 1) {
      setShowUpgradeModal(true);
      return;
    }

    setIsGenerating(true);
    try {
      await generateMutation.mutateAsync({ prompt, modelId, parameters, referenceImages });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="bg-background">
      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        creditsRemaining={balance && "credits" in balance ? balance.credits : 0}
        subscription={subscription}
        region={region}
      />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="space-y-12">
          {/* Generation Canvas Section */}
          <section className="mx-auto max-w-4xl">
            <GenerationCanvas
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              creditsRemaining={
                balanceLoading
                  ? undefined
                  : balance && "credits" in balance ? balance.credits : undefined
              }
              creditTier={
                balanceLoading
                  ? undefined
                  : balance && "tier" in balance ? balance.tier : undefined
              }
            />
          </section>

          {/* Gallery Section - Loads in background, doesn't block form */}
          {session?.user && <RecentGenerations userId={session.user.id} />}
        </div>
      </main>
    </div>
  );
}
